import { useContext } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { EProductType, UserRole } from '@leedpulse-monorepo/enums';
import AuthenticationContext from './contexts/auth/AuthenticationContext';
import StartLayout from './layouts/StartLayout';
import DashboardLayout from './layouts/DashboardLayout';
import SignInPage, { SIGN_IN_PAGE_PATH } from './pages/SignInPage';
import OverviewPage, {
  OVERVIEW_PAGE_PATH,
  OVERVIEW_PAGE_TITLE,
} from './pages/OverviewPage';
import UsersPage, {
  USERS_PAGE_PATH,
  USERS_PAGE_TITLE,
} from './pages/users/UsersPage';
import UserDetailPage from './pages/users/UserDetailPage';
import SubscriptionsPage, {
  SUBSCRIPTIONS_PAGE_PATH,
  SUBSCRIPTIONS_PAGE_TITLE,
} from './pages/subscriptions/SubscriptionsPage';
import SubscriptionDetailPage from './pages/subscriptions/SubscriptionDetailPage';
import ContactsPage, {
  CONTACTS_PAGE_PATH,
  CONTACTS_PAGE_TITLE,
} from './pages/contacts/ContactsPage';
import ContactDetailPage from './pages/contacts/ContactDetailPage';
import NumbersPage, {
  NUMBERS_PAGE_PATH,
  NUMBERS_PAGE_TITLE,
} from './pages/numbers/NumbersPage';
import NumberDetailPage from './pages/numbers/NumberDetailPage';

import AIDetailPage, {
  AI_DETAIL_PAGE_PATH,
} from './pages/subscriptions/ai/AIDetailPage';
import ChatPage, { CHAT_PAGE_PATH } from './pages/contacts/chat/ChatPage';
import SubscriptionChatsPage, {
  SUBSCRIPTIONS_CHATS_PAGE_PATH,
} from './pages/subscriptions/SubscriptionChatsPage';
import { SubscriptionChatsProvider } from './contexts/data/SubscriptionChatsContext';
import DomainsPage, {
  DOMAINS_PAGE_PATH,
  DOMAINS_PAGE_TITLE,
} from './pages/domains/DomainsPage';
import DomainDetailPage from './pages/domains/DomainDetailPage';
import ScenarioDetailPage, {
  SCENARIO_PAGE_PATH,
} from './pages/scenarios/ScenarioDetailPage';
import PlansPage, {
  PLANS_PAGE_PATH,
  PLANS_PAGE_TITLE
} from './pages/plans/PlansPage';
import PlanDetailPage from './pages/plans/PlanDetailPage';
import VoipCallsPage, {
  VOIP_CALLS_PAGE_PATH,
  VOIP_CALLS_PAGE_TITLE,
} from './pages/leedflow/VoipCallsPage/VoipCallsPage';
import FilesPage, {
  FILES_PAGE_PATH,
  FILES_PAGE_TITLE,
} from './pages/files/FilesPage';
import { AuditOutlined, BankOutlined, BookOutlined, BugOutlined, CalendarOutlined, ContactsOutlined, EuroCircleOutlined, FileTextOutlined, HomeOutlined, PhoneOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import CompaniesPage, { COMPANIES_PAGE_PATH, COMPANIES_PAGE_TITLE } from './pages/companies/CompaniesPage';
import CompanyDetailPage from './pages/companies/CompanyDetailPage';
import E2EResultsPage, { E2E_RESULTS_PAGE_PATH, E2E_RESULTS_PAGE_TITLE } from './pages/e2e/E2EResultsPage';
import E2EResultDetailPage from './pages/e2e/E2EResultDetailPage';

const E2E_STAFF_ROLES = [UserRole.owner, UserRole.admin, UserRole.maintainer];

export interface NavigationItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  iconActive?: React.ReactNode;
  /** When set, only users with this exact role can see and access this route */
  requiredRole?: UserRole;
  allowedRoles?: UserRole[];
  /** Optional nested menu items rendered as a SubMenu */
  children?: NavigationItem[];
}

export const navigationConfig: NavigationItem[] = [
  {
    title: OVERVIEW_PAGE_TITLE,
    path: OVERVIEW_PAGE_PATH,
    icon: <HomeOutlined />,
    iconActive: <HomeOutlined />,
  },
  {
    title: COMPANIES_PAGE_TITLE,
    path: COMPANIES_PAGE_PATH,
    icon: <BankOutlined />,
    iconActive: <BankOutlined />,
    children: [
      {
        title: SUBSCRIPTIONS_PAGE_TITLE,
        path: SUBSCRIPTIONS_PAGE_PATH,
        icon: <CalendarOutlined />,
        iconActive: <CalendarOutlined />,
      },
      {
        title: CONTACTS_PAGE_TITLE,
        path: CONTACTS_PAGE_PATH,
        icon: <ContactsOutlined />,
        iconActive: <ContactsOutlined />,
      },
      {
        title: USERS_PAGE_TITLE,
        path: USERS_PAGE_PATH,
        icon: <UserOutlined />,
        iconActive: <UserOutlined />,
      },
      {
        title: PLANS_PAGE_TITLE,
        path: PLANS_PAGE_PATH,
        icon: <EuroCircleOutlined />,
        iconActive: <EuroCircleOutlined />,
      },
    ],
  },
  {
    title: DOMAINS_PAGE_TITLE,
    path: DOMAINS_PAGE_PATH,
    icon: <BookOutlined />,
    iconActive: <BookOutlined />,
  },
  {
    title: NUMBERS_PAGE_TITLE,
    path: NUMBERS_PAGE_PATH,
    icon: <PhoneOutlined />,
    iconActive: <PhoneOutlined />,
  },
  {
    title: VOIP_CALLS_PAGE_TITLE,
    path: VOIP_CALLS_PAGE_PATH,
    icon: <AuditOutlined />,
    iconActive: <AuditOutlined />,
    children: [
      {
        title: E2E_RESULTS_PAGE_TITLE,
        path: E2E_RESULTS_PAGE_PATH,
        icon: <BugOutlined />,
        iconActive: <BugOutlined />,
        allowedRoles: E2E_STAFF_ROLES,
      },
    ],
  },
  {
    title: FILES_PAGE_TITLE,
    path: FILES_PAGE_PATH,
    icon: <FileTextOutlined />,
    iconActive: <FileTextOutlined />,
  },
];

export const PUBLIC_ROUTES = [
  {
    path: SIGN_IN_PAGE_PATH,
    element: <SignInPage />,
  },
];

interface RoleGuardProps {
  requiredRole?: UserRole;
  allowedRoles?: UserRole[];
  children: React.ReactNode;
}

/**
 * Prevent old routes with theming to be death links
 */
const LegacyPrefixRedirect: React.FC = () => {
  const location = useLocation();
  const target =
    location.pathname.replace(/^\/(leedpulse|leedflow)(?=\/|$)/, '') || '/';

  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

const RoleGuard: React.FC<RoleGuardProps> = ({ requiredRole, allowedRoles, children }) => {
  const authContext = useContext(AuthenticationContext);

  const role = authContext.authentication?.role;
  if ((requiredRole !== undefined && role !== requiredRole) || (allowedRoles && (!role || !allowedRoles.includes(role)))) {
    return <Navigate to={OVERVIEW_PAGE_PATH} replace />;
  }

  return children as React.ReactElement;
};

const AppRouter: React.FC = () => {
  return (
    <Routes>
      <Route element={<StartLayout />}>
        {PUBLIC_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Route>

      <Route element={<DashboardLayout />}>
        <Route path={E2E_RESULTS_PAGE_PATH} element={<RoleGuard allowedRoles={E2E_STAFF_ROLES}><E2EResultsPage /></RoleGuard>} />
        <Route path={`${E2E_RESULTS_PAGE_PATH}/:runKey`} element={<RoleGuard allowedRoles={E2E_STAFF_ROLES}><E2EResultDetailPage /></RoleGuard>} />
        <Route
          path={OVERVIEW_PAGE_PATH}
          element={<OverviewPage />}
        />

        <Route
          path={`${VOIP_CALLS_PAGE_PATH}`}
          element={<VoipCallsPage />}
        />

        <Route
          path={USERS_PAGE_PATH}
          element={<UsersPage />}
        />
        <Route
          path={USERS_PAGE_PATH + '/new'}
          element={<UserDetailPage isCreateMode={true} />}
        />
        <Route
          path={USERS_PAGE_PATH + '/:userId'}
          element={<UserDetailPage isCreateMode={false} />}
        />
        <Route
          path={COMPANIES_PAGE_PATH}
          element={<CompaniesPage />}
        />
        <Route
          path={COMPANIES_PAGE_PATH + '/:companyId'}
          element={<CompanyDetailPage isCreateMode={false} />}
        />
        <Route
          path={COMPANIES_PAGE_PATH + '/new'}
          element={<CompanyDetailPage isCreateMode={true} />}
        />
        <Route
          path={SUBSCRIPTIONS_PAGE_PATH}
          element={<SubscriptionsPage />}
        />
        {/* NOTE : We do not create subscriptions manually, so we do not need to create a
        new subscription page */}
        <Route
          path={SUBSCRIPTIONS_PAGE_PATH + '/:subscriptionId'}
          element={<SubscriptionDetailPage />}
        />
        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH +
            '/:subscriptionId' +
            SUBSCRIPTIONS_CHATS_PAGE_PATH
          }
          element={
            <SubscriptionChatsProvider>
              <SubscriptionChatsPage />
            </SubscriptionChatsProvider>
          }
        />
        <Route
          path={CONTACTS_PAGE_PATH}
          element={<ContactsPage withIdScope={false} />}
        />
        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH + '/:subscriptionId' + CONTACTS_PAGE_PATH
          }
          element={<ContactsPage withIdScope={true} />}
        />
        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH +
            '/:subscriptionId' +
            CONTACTS_PAGE_PATH +
            '/new'
          }
          element={
            <ContactDetailPage
              isCreateMode={true}
              isImportMode={false}
              withSubscriptionIdScope={true}
            />
          }
        />
        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH +
            '/:subscriptionId' +
            CONTACTS_PAGE_PATH +
            '/import'
          }
          element={
            <ContactDetailPage
              isCreateMode={false}
              isImportMode={true}
              withSubscriptionIdScope={true}
            />
          }
        />
        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH +
            '/:subscriptionId' +
            CONTACTS_PAGE_PATH +
            '/:contactId'
          }
          element={
            <ContactDetailPage
              isCreateMode={false}
              isImportMode={false}
              withSubscriptionIdScope={true}
            />
          }
        />
        <Route
          path={CONTACTS_PAGE_PATH + '/:contactId'}
          element={
            <ContactDetailPage
              isCreateMode={false}
              isImportMode={false}
              withSubscriptionIdScope={false}
            />
          }
        />

        <Route path={PLANS_PAGE_PATH} element={<PlansPage />} />
        <Route path={PLANS_PAGE_PATH + '/new'} element={<PlanDetailPage isCreateMode={true} />} />
        <Route path={PLANS_PAGE_PATH + '/:planId'} element={<PlanDetailPage isCreateMode={false} />} />

        <Route path={NUMBERS_PAGE_PATH} element={<NumbersPage />} />
        <Route
          path={NUMBERS_PAGE_PATH}
          element={<NumbersPage />}
        />
        <Route
          path={NUMBERS_PAGE_PATH + '/new'}
          element={<NumberDetailPage isCreateMode={true} />}
        />
        <Route
          path={NUMBERS_PAGE_PATH + '/:numberId'}
          element={<NumberDetailPage isCreateMode={false} />}
        />

        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH +
            '/:subscriptionId' +
            CONTACTS_PAGE_PATH +
            '/:contactId/' +
            CHAT_PAGE_PATH
          }
          element={<ChatPage withSubscriptionIdScope={true} />}
        />

        <Route
          path={
            CONTACTS_PAGE_PATH + '/:contactId/' + CHAT_PAGE_PATH
          }
          element={<ChatPage withSubscriptionIdScope={false} />}
        />

        <Route
          path={
            SUBSCRIPTIONS_PAGE_PATH + '/:subscriptionId' + AI_DETAIL_PAGE_PATH
          }
          element={<AIDetailPage />}
        />
        <Route
          path={FILES_PAGE_PATH}
          element={<FilesPage />}
        />
        <Route
          path={DOMAINS_PAGE_PATH}
          element={<DomainsPage />}
        />

        <Route
          path={DOMAINS_PAGE_PATH + '/new'}
          element={<DomainDetailPage isCreateMode={true} />}
        />
        <Route
          path={DOMAINS_PAGE_PATH + '/:domainId'}
          element={<DomainDetailPage isCreateMode={false} />}
        />

        <Route
          path={
            DOMAINS_PAGE_PATH + '/:domainId' + SCENARIO_PAGE_PATH + '/new'
          }
          element={<ScenarioDetailPage isCreateMode={true} />}
        />

        <Route
          path={
            DOMAINS_PAGE_PATH +
            '/:domainId' +
            SCENARIO_PAGE_PATH +
            '/:scenarioId'
          }
          element={<ScenarioDetailPage isCreateMode={false} />}
        />
      </Route>

      <Route path="/leedpulse/*" element={<LegacyPrefixRedirect />} />
      <Route path="/leedflow/*" element={<LegacyPrefixRedirect />} />

      <Route path={`*`} element={<Navigate to={`${OVERVIEW_PAGE_PATH}?app=${EProductType.LEEDFLOW}`} replace />} />
    </Routes>
  );
};

export default AppRouter;
